import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Chip,
    Avatar,
    Grid,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Skeleton,
    CircularProgress
} from '@mui/material';


import {
    AccessTime as AccessTimeIcon,
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import Seo from '../../components/common/Seo';
import { sampleBlogPosts } from './data/samplePosts';
import { useTheme } from '@mui/material/styles';
import BlogPromotion from '../../components/blog/BlogPromotion';

function BlogPost() {
    const { slug } = useParams();
    const location = useLocation();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();

    // ハッシュに基づくスクロール処理
    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location.hash]);

    useEffect(() => {
        setLoading(true);

        // 本番環境ではAPIからデータを取得する
        // 現段階ではサンプルデータを使用
        setTimeout(() => {
            const foundPost = sampleBlogPosts.find(post => post.slug === slug);
            setPost(foundPost);

            // 関連記事の取得（同じカテゴリーの記事から3つまで、現在の記事を除く）
            if (foundPost) {
                // 既に処理済みの関連記事オブジェクトがある場合はそちらを使用
                if (foundPost._relatedPostObjects && foundPost._relatedPostObjects.length > 0) {
                    setRelatedPosts(foundPost._relatedPostObjects);
                } else {
                    // 従来の方法で関連記事を取得
                    const related = sampleBlogPosts
                        .filter(p =>
                            p.slug !== slug &&
                            p.categories.some(cat => foundPost.categories.includes(cat))
                        )
                        .slice(0, 3);

                    setRelatedPosts(related);
                }
            }

            setLoading(false);
        }, 500); // 少し遅延を入れてローディング表示を見せる

        // ページトップへスクロール
        window.scrollTo(0, 0);
    }, [slug]);

    // 画像エラー時の処理
    const handleImageError = (e) => {
        e.target.src = '/images/blog/default-cover.jpg';
    };

    // アバター画像エラー時の処理
    const handleAvatarError = (e) => {
        e.target.src = '/images/avatars/default-avatar.jpg';
    };

    // HTMLコンテンツ内の画像を処理する関数
    const processContentImages = () => {
        if (!post) return;

        // 記事コンテンツがレンダリングされた後に実行
        setTimeout(() => {
            // ブログ記事のコンテンツ内のすべての画像要素を対象
            const contentImages = document.querySelectorAll('.blog-content img');

            contentImages.forEach(img => {
                // 元のsrcを保存
                const originalSrc = img.getAttribute('src');

                // すでにエラーハンドラが設定されていない場合のみ設定
                if (!img.onerror) {
                    img.onerror = function () {
                        // 画像のパスがblog関連であれば、デフォルト画像を表示
                        if (originalSrc.includes('/images/blog/')) {
                            // alt属性を取得して意味のある代替テキストを設定
                            const altText = img.alt || '画像';

                            // 元の画像のスタイル属性を取得
                            const originalStyleStr = img.getAttribute('style') || '';

                            // 画像の幅と高さを取得し、CSSに変換
                            const width = img.width ? `width: ${img.width}px;` : '';
                            const height = img.height ? `height: auto;` : '';

                            // カスタムスタイルを組み合わせる
                            const combinedStyle = `${originalStyleStr} ${width} ${height} object-fit: cover;`;

                            // デフォルト画像を設定
                            this.src = '/images/blog/default-cover.jpg';
                            this.alt = `${altText}（代替画像）`;
                            this.style = combinedStyle;
                        } else {
                            // blog関連でない画像は非表示
                            this.style.display = 'none';
                        }
                    };
                }
            });
        }, 100);
    };

    // 記事内容内の画像を処理するための副作用
    useEffect(() => {
        processContentImages();
    }, [post]);

    // DOMの変更を監視し、動的に追加された画像も処理する
    useEffect(() => {
        if (!post) return;

        // MutationObserverの設定
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    processContentImages();
                }
            });
        });

        // 監視開始
        const blogContent = document.querySelector('.blog-content');
        if (blogContent) {
            observer.observe(blogContent, { childList: true, subtree: true });
        }

        // クリーンアップ関数
        return () => {
            observer.disconnect();
        };
    }, [post]);

    // ローディング表示
    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={60} thickness={4} sx={{ mb: 4 }} />
                <Typography variant="h5" color="text.secondary" align="center">
                    記事を読み込んでいます...
                </Typography>
            </Container>
        );
    }

    // 記事が見つからない場合
    if (!post) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom>
                    記事が見つかりません
                </Typography>
                <Typography variant="body1" paragraph>
                    お探しの記事は削除されたか、移動された可能性があります。
                </Typography>
                <Button
                    component={Link}
                    to="/blog"
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    sx={{ mt: 2 }}
                >
                    ブログトップに戻る
                </Button>
            </Container>
        );
    }

    // 記事のcoverImageが設定されていない場合のデフォルト値
    const coverImage = post.coverImage || '/images/blog/default-cover.jpg';

    // 著者のアバター画像が設定されていない場合のデフォルト値
    const authorAvatar = post.author.avatar || post.author.profilePicture || '/images/avatars/default-avatar.jpg';

    return (
        <>
            <Seo
                title={`${post.title} | ルームシェアブログ`}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                keywords={post.tags.join(',') + ',ルームシェア,ブログ'}
                ogImage={coverImage}
            />

            <Box
                sx={{
                    backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${coverImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    py: { xs: 8, md: 12 },
                    color: 'text.primary',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Container maxWidth="md">
                    <Button
                        component={Link}
                        to="/blog"
                        sx={{
                            color: 'text.primary',
                            mb: 3,
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                        startIcon={<ArrowBackIcon />}
                    >
                        ブログ一覧に戻る
                    </Button>

                    <Box sx={{ mb: 2 }}>
                        {post.categories.map((category) => (
                            <Chip
                                key={category}
                                label={category}
                                component={Link}
                                to={`/blog?category=${category}`}
                                sx={{
                                    mr: 1,
                                    mb: 1,
                                    backgroundColor: 'background.paper',
                                    color: 'text.primary',
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                                clickable
                            />
                        ))}
                    </Box>

                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '2.5rem' }
                        }}
                    >
                        {post.title}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="xl">
                <Box
                    sx={{
                        py: { xs: 3, md: 5 },
                        mb: 8,
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1,
                            my: 2
                        },
                        '& h2': {
                            mt: 4,
                            mb: 2,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            fontWeight: 600,
                            color: theme.palette.primary.main
                        },
                        '& h3': {
                            mt: 3,
                            mb: 1.5,
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                            fontWeight: 500
                        },
                        '& p': {
                            mb: 2,
                            fontSize: '1.05rem',
                            lineHeight: 1.7
                        },
                        '& ul, & ol': {
                            mb: 2,
                            pl: 4
                        },
                        '& li': {
                            mb: 1
                        },
                        '& blockquote': {
                            borderLeft: `4px solid ${theme.palette.primary.light}`,
                            pl: 2,
                            py: 1,
                            my: 3,
                            fontStyle: 'italic',
                            bgcolor: 'rgba(0, 0, 0, 0.03)',
                            borderRadius: 1
                        }
                    }}
                >
                    {post.contentComponent ? <post.contentComponent /> : (
                        <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
                    )}

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>タグ</Typography>
                        <Box>
                            {post.tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    component={Link}
                                    to={`/blog?tag=${tag}`}
                                    size="small"
                                    sx={{ mr: 1, mb: 1 }}
                                    clickable
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* サービス誘導コンポーネント */}
                    <BlogPromotion />
                </Box>

                {relatedPosts.length > 0 && (
                    <Box sx={{ mb: 8, maxWidth: 'xl', mx: 'auto' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                            関連記事
                        </Typography>
                        <Divider sx={{ mb: 4 }} />

                        <Grid container spacing={3}>
                            {relatedPosts.map((relatedPost) => (
                                <Grid item xs={12} sm={6} md={4} key={relatedPost.slug}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardActionArea
                                            component={Link}
                                            to={`/blog/${relatedPost.slug}`}
                                            sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="160"
                                                image={relatedPost.coverImage || '/images/blog/default-cover.jpg'}
                                                alt={relatedPost.title}
                                                onError={handleImageError}
                                            />
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography
                                                    gutterBottom
                                                    variant="h6"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {relatedPost.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {relatedPost.excerpt}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Container>
        </>
    );
}

export default BlogPost; 